import { useEffect, useRef } from "react";

export function useTableSync() {
  const t1Ref = useRef<HTMLDivElement>(null);
  const t2Ref = useRef<HTMLDivElement>(null);
  const t3Ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const tables = [t1Ref.current, t2Ref.current, t3Ref.current].filter(
      (el): el is HTMLDivElement => el !== null,
    );

    if (tables.length < 2) return;

    let isSyncing = false;
    let rAFId: number | null = null;
    let pendingSource: HTMLDivElement | null = null;

    const canScrollX = (el: HTMLDivElement) => el.scrollWidth > el.clientWidth + 1;

    const syncScroll = (source: HTMLDivElement) => {
      isSyncing = true;
      const { scrollTop, scrollLeft } = source;
      const sourceScrollsX = canScrollX(source);

      tables.forEach((target) => {
        if (target === source) return;

        if (target.scrollTop !== scrollTop) {
          target.scrollTop = scrollTop;
        }

        if (sourceScrollsX && canScrollX(target) && target.scrollLeft !== scrollLeft) {
          target.scrollLeft = scrollLeft;
        }
      });

      isSyncing = false;
      rAFId = null;
      pendingSource = null;
    };

    const handleScroll = (e: Event) => {
      if (isSyncing) return;

      pendingSource = e.currentTarget as HTMLDivElement;
      if (!rAFId) {
        rAFId = requestAnimationFrame(() => {
          if (pendingSource) syncScroll(pendingSource);
        });
      }
    };

    tables.forEach((t) => {
      t.addEventListener("scroll", handleScroll, { passive: true });
    });

    return () => {
      if (rAFId) cancelAnimationFrame(rAFId);
      tables.forEach((t) => {
        t.removeEventListener("scroll", handleScroll);
      });
    };
  }, []);

  return { t1Ref, t2Ref, t3Ref };
}
